import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { EmptyState, ErrorState } from './states';

declare module '@tanstack/react-table' {
  // Extra per-column presentation, read by DataTable. `TData`/`TValue` must be
  // declared to merge with the library's own interface even though unused.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'start' | 'center' | 'end';
    /** Applied to both the header and body cells of the column. */
    className?: string;
    headerClassName?: string;
    /** Fixed width, e.g. `140` or `'12rem'`. */
    width?: number | string;
  }
}

export type { ColumnDef } from '@tanstack/react-table';

export interface SortState {
  field: string;
  order: 'asc' | 'desc';
}

export interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
  pageSizeOptions?: number[];
}

export interface DataTableProps<T> {
  /**
   * Column definitions. A column is sortable when it sets `enableSorting: true`;
   * its `id` is what `onSortChange` reports, so make it the backend's sort key.
   */
  columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Rows for the current page only when paginated. */
  data: T[] | undefined;
  getRowId: (row: T) => string;

  loading?: boolean;
  error?: unknown;
  errorMessage?: React.ReactNode;
  onRetry?: () => void;

  /** Server-driven pagination. Omit for a short local table. */
  pagination?: DataTablePagination;

  sort?: SortState | null;
  /**
   * Called when the user clicks a sortable header. Push it into URL state and
   * feed it back to the query — never sort `data` yourself.
   */
  onSortChange?: (sort: SortState | null) => void;

  rowSelection?: {
    selected: string[];
    onChange: (ids: string[]) => void;
    /** Rows that cannot be ticked, e.g. an import candidate already on the roster. */
    isDisabled?: (row: T) => boolean;
  };
  onRowClick?: (row: T) => void;
  /** Extra classes per row — a highlighted or muted state. */
  rowClassName?: (row: T) => string | undefined;

  emptyState?: React.ReactNode;
  /** A summary row under the body — totals, a sum of bonuses. */
  footer?: React.ReactNode;
  /** Caps the body height and scrolls inside the card, header pinned. */
  maxHeight?: number | string;
  /** Drops the card chrome, for a table that already sits inside a card. */
  plain?: boolean;
  className?: string;
  /** Number of skeleton rows while the first page loads. */
  skeletonRows?: number;
}

/**
 * The one table in the app.
 *
 * What it adds over the raw primitives is what every list page needs and
 * always gets wrong when hand-rolled: consistent error and empty states, and
 * sorting and pagination that are *server-driven*. The API returns one page at
 * a time, so a client-side sort would reorder 20 visible rows while claiming
 * to have sorted thousands.
 */
export function DataTable<T>({
  columns,
  data,
  getRowId,
  loading = false,
  error,
  errorMessage,
  onRetry,
  pagination,
  sort,
  onSortChange,
  rowSelection,
  onRowClick,
  rowClassName,
  emptyState,
  footer,
  maxHeight,
  plain = false,
  className,
  skeletonRows = 6,
}: DataTableProps<T>) {
  const rows = React.useMemo(() => data ?? [], [data]);

  const sorting: SortingState = sort ? [{ id: sort.field, desc: sort.order === 'desc' }] : [];

  const selectionState: RowSelectionState = React.useMemo(
    () => Object.fromEntries((rowSelection?.selected ?? []).map((id) => [id, true])),
    [rowSelection?.selected],
  );

  const allColumns = React.useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!rowSelection) return columns;

    const select: ColumnDef<T, unknown> = {
      id: '__select',
      enableSorting: false,
      meta: { width: 40, className: 'pe-0' },
      header: ({ table }) => (
        <Checkbox
          aria-label="Select all"
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onClick={(event) => event.stopPropagation()}
          onCheckedChange={(value) => row.toggleSelected(value === true)}
        />
      ),
    };

    return [select, ...columns];
  }, [columns, rowSelection]);

  const table = useReactTable({
    data: rows,
    columns: allColumns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    enableSorting: Boolean(onSortChange),
    enableSortingRemoval: true,
    enableMultiSort: false,
    // Columns opt in to sorting individually; without this every column would
    // render as clickable.
    defaultColumn: { enableSorting: false },
    state: { sorting, rowSelection: selectionState },
    enableRowSelection: rowSelection?.isDisabled
      ? (row) => !rowSelection.isDisabled?.(row.original)
      : Boolean(rowSelection),
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      onSortChange?.(first ? { field: first.id, order: first.desc ? 'desc' : 'asc' } : null);
    },
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(selectionState) : updater;
      rowSelection?.onChange(Object.keys(next).filter((id) => next[id]));
    },
  });

  if (error) {
    return (
      <div
        className={cn(!plain && 'rounded-xl border bg-card shadow-xs dark:shadow-none', className)}
      >
        <ErrorState
          message={errorMessage ?? (error instanceof Error ? error.message : undefined)}
          {...(onRetry ? { onRetry } : {})}
        />
      </div>
    );
  }

  const showSkeleton = loading && rows.length === 0;
  const colCount = table.getVisibleLeafColumns().length;

  return (
    <div className={cn('grid gap-3', className)}>
      <Table
        containerClassName={cn(
          plain && 'rounded-none border-0 bg-transparent shadow-none',
          maxHeight !== undefined && 'overflow-y-auto',
          // Refetching with rows already on screen: dim rather than blank them,
          // so the page doesn't jump while the next one loads.
          loading && rows.length > 0 && 'opacity-60 transition-opacity',
        )}
        containerStyle={maxHeight !== undefined ? { maxHeight } : undefined}
      >
        <TableHeader className={cn(maxHeight !== undefined && 'sticky top-0 z-10 [&_tr]:bg-muted')}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const canSort = header.column.getCanSort();
                const dir = header.column.getIsSorted();

                return (
                  <TableHead
                    key={header.id}
                    style={meta?.width !== undefined ? { width: meta.width } : undefined}
                    className={cn(alignClass(meta?.align), meta?.className, meta?.headerClassName)}
                    aria-sort={
                      dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined
                    }
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn(
                          'inline-flex items-center gap-1 uppercase transition-colors hover:text-foreground',
                          dir && 'text-foreground',
                          meta?.align === 'end' && 'flex-row-reverse',
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {dir === 'asc' ? (
                          <ArrowUpIcon className="size-3" />
                        ) : dir === 'desc' ? (
                          <ArrowDownIcon className="size-3" />
                        ) : (
                          <ArrowUpDownIcon className="size-3 opacity-50" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {showSkeleton ? (
            Array.from({ length: skeletonRows }, (_, index) => (
              <TableRow key={`skeleton-${index}`} className="hover:bg-transparent">
                {Array.from({ length: colCount }, (__, cell) => (
                  <TableCell key={cell}>
                    <Skeleton className="h-4 w-full max-w-40" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={colCount} className="h-auto py-2 whitespace-normal">
                {emptyState ?? <EmptyState />}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn(onRowClick && 'cursor-pointer', rowClassName?.(row.original))}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta;
                  return (
                    <TableCell
                      key={cell.id}
                      style={meta?.width !== undefined ? { width: meta.width } : undefined}
                      className={cn(alignClass(meta?.align), meta?.className)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>

        {footer && rows.length > 0 ? <TableFooter>{footer}</TableFooter> : null}
      </Table>

      {pagination ? <PaginationBar {...pagination} /> : null}
    </div>
  );
}

function alignClass(align: 'start' | 'center' | 'end' | undefined) {
  if (align === 'end') return 'text-end';
  if (align === 'center') return 'text-center';
  return undefined;
}

function PaginationBar({
  page,
  pageSize,
  total,
  onChange,
  pageSizeOptions = [10, 20, 50, 100],
}: DataTablePagination) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const options = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <Select value={String(pageSize)} onValueChange={(value) => onChange(1, Number(value))}>
          <SelectTrigger size="sm" className="w-[4.5rem]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="tabular-nums">
          {total === 0
            ? '0 results'
            : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
        </span>
        <span className="hidden text-border sm:inline">|</span>
        <span className="tabular-nums">
          Page {Math.min(page, pageCount)} of {pageCount}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onChange(page - 1, pageSize)}
        >
          <ChevronLeftIcon className="rtl:rotate-180" />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Next page"
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1, pageSize)}
        >
          <ChevronRightIcon className="rtl:rotate-180" />
        </Button>
      </div>
    </div>
  );
}
